import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { X, CheckCircle, AlertTriangle, Info, XCircle, AlertCircle } from 'lucide-react';
import { useNotifications } from '../../features/organization/hooks/useNotifications'; // Backend hook
import { useUser } from '../../features/auth/hooks/useUser';
import { useGenerationStore } from '../../store/generation/generationStore';

// Individual toast notification item
const ToastNotificationItem = ({ notification, onClose }) => {
  const navigate = useNavigate();
  const [isExiting, setIsExiting] = useState(false);
  const timerRef = useRef(null);
  const animationTimeoutRef = useRef(null);
  
  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type?.toUpperCase()) {
      case 'SUCCESS':
        return <CheckCircle size={20} />;
      case 'ERROR':
      case 'FAILURE':
        return <XCircle size={20} />;
      case 'WARNING':
        return <AlertTriangle size={20} />;
      case 'CRITICAL':
        return <AlertCircle size={20} />;
      case 'INFO':
        return <Info size={20} />;
      default:
        return <Info size={20} />;
    }
  };

  // Get notification type class for CSS styling
  const getNotificationTypeClass = (type) => {
    const normalizedType = type?.toUpperCase();
    switch (normalizedType) {
      case 'SUCCESS':
        return 'success';
      case 'ERROR':
      case 'FAILURE':
        return 'error';
      case 'WARNING':
        return 'warning';
      case 'CRITICAL':
        return 'critical';
      case 'INFO':
      default:
        return 'info';
    }
  };

  // Wrap handleClose in useCallback to stabilize it between renders
  const handleClose = useCallback(() => {
    // Clear the auto-dismiss timer if it exists
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    
    // Start exit animation
    setIsExiting(true);
    
    // Wait for animation to complete before removing
    animationTimeoutRef.current = setTimeout(() => {
      onClose(notification.id);
    }, 300); // Match this with the CSS animation duration
  }, [onClose, notification.id]);

  // Auto-dismiss timer (longer for critical/error messages)
  useEffect(() => {
    const dismissTime = notification.type?.toUpperCase() === 'CRITICAL' || 
                       notification.type?.toUpperCase() === 'ERROR' || 
                       notification.type?.toUpperCase() === 'FAILURE' 
                       ? 8000 // 8 seconds for important messages
                       : 5000; // 5 seconds for others

    timerRef.current = setTimeout(() => {
      handleClose();
    }, dismissTime);
    
    // Clear both timeouts on unmount
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    };
  }, [notification.id, notification.type, handleClose]);

  const typeClass = getNotificationTypeClass(notification.type);

  const handleClick = () => {
    if (notification.actionUrl) {
      handleClose();

      // For project-chat routes, build navigation state from generation store
      // so ProjectChatScreen can restore without location.state from the original navigation
      if (notification.actionUrl.includes('/project-chat/')) {
        const session = useGenerationStore.getState().activeScriptGeneration;
        if (session) {
          navigate(notification.actionUrl, {
            state: {
              projectData: session.projectData,
              conversationId: session.conversationId,
              fromProjectView: session.fromProjectView,
              loadExistingMessages: true,
              isTestSuiteMode: session.isTestSuiteMode,
              existingProjectId: session.existingProjectId,
              existingProjectName: session.existingProjectName
            }
          });
          return;
        }
      }

      navigate(notification.actionUrl);
    }
  };

  const isClickable = !!notification.actionUrl;

  return (
    <div
      className={`toast-notification-item ${typeClass} ${isExiting ? 'exiting' : ''} ${isClickable ? 'clickable' : ''}`}
      style={{
        cursor: isClickable ? 'pointer' : 'default'
      }}
      onClick={isClickable ? handleClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <div className="toast-notification-icon">
        {getNotificationIcon(notification.type)}
      </div>
      <div className="toast-notification-content">
        <h4 className="toast-notification-title">{notification.title}</h4>
        <div className="toast-notification-message">
          {notification.description.split('\n').map((line, idx) => (
            <p key={`${notification.id}-line-${idx}`}>{line}</p>
          ))}
        </div>
        {isClickable && (
          <span className="toast-notification-action-hint">Click to view</span>
        )}
      </div>
      <button
        className="toast-notification-close-button"
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        aria-label="Close notification"
      >
        <X size={16} />
      </button>
    </div>
  );
};

ToastNotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    type: PropTypes.string,
    actionUrl: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func.isRequired
};

// SEPARATE component to handle animations
const AnimatedToastNotification = ({ notification, onClose }) => {
  return (
    <ToastNotificationItem
      key={notification.id}
      notification={notification}
      onClose={onClose}
    />
  );
};

AnimatedToastNotification.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    type: PropTypes.string,
    actionUrl: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func.isRequired
};

// Custom hook to manage toast notifications with backend integration
export const useToastNotifications = () => {
  const { userData } = useUser();
  const userId = userData?.userId || userData?.id || userData?.user_id;
  const { handleCreateNotification } = useNotifications(userId);
  
  const [notificationList, setNotificationList] = useState([]);

  const addNotification = async (notification) => {
    // Create a truly unique ID that includes random component
    const id = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const newNotification = { 
      ...notification, 
      id,
      read: false // Always start as unread for toast notifications
    };
    
    // Add to local state for immediate UI feedback
    setNotificationList(currentList => [...currentList, newNotification]);
    
    // Try to create in backend (optional - don't block UI if it fails)
    try {
      if (userId && handleCreateNotification) {
        await handleCreateNotification({
          title: notification.title,
          description: notification.description,
          type: notification.type || 'INFO',
          sourceId: notification.sourceId || '',
          actionUrl: notification.actionUrl || ''
        });
      }
    } catch (error) {
      // Don't remove from UI even if backend fails
    }
  };

  const removeNotification = (idToRemove) => {
    setNotificationList(currentList => {
      return currentList.filter(notification => notification.id !== idToRemove);
    });
  };

  // Convenience methods for different notification types
  const showSuccess = async (title, description, actionUrl = '', sourceId = '') => {
    await addNotification({
      title,
      description,
      type: 'SUCCESS',
      actionUrl,
      sourceId
    });
  };

  const showError = async (title, description, actionUrl = '', sourceId = '') => {
    await addNotification({
      title,
      description,
      type: 'ERROR',
      actionUrl,
      sourceId
    });
  };

  const showWarning = async (title, description, actionUrl = '', sourceId = '') => {
    await addNotification({
      title,
      description,
      type: 'WARNING',
      actionUrl,
      sourceId
    });
  };

  const showInfo = async (title, description, actionUrl = '', sourceId = '') => {
    await addNotification({
      title,
      description,
      type: 'INFO',
      actionUrl,
      sourceId
    });
  };

  const showCritical = async (title, description, actionUrl = '', sourceId = '') => {
    await addNotification({
      title,
      description,
      type: 'CRITICAL',
      actionUrl,
      sourceId
    });
  };

  const showFailure = async (title, description, actionUrl = '', sourceId = '') => {
    await addNotification({
      title,
      description,
      type: 'FAILURE',
      actionUrl,
      sourceId
    });
  };

  return {
    notifications: notificationList,
    addNotification,
    removeNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showCritical,
    showFailure
  };
};

// Main toast notification container component
const ToastNotification = ({ notifications, onClose }) => {
  return (
    <div className="toast-notification-wrapper">
      {notifications.map(notification => (
        <AnimatedToastNotification
          key={notification.id}
          notification={notification}
          onClose={onClose}
        />
      ))}
    </div>
  );
};

ToastNotification.propTypes = {
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      type: PropTypes.string,
      actionUrl: PropTypes.string
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired
};

export default ToastNotification;